<template>
    <div class="bg-color rounded-5 p-5">
        <div class="w-100 rounded-4">
            <div
                class="d-flex justify-content-between"
                style="background: rgb(241, 247, 255); border-radius: 27px 27px 0 0"
            >
                <div class="flex align-items-center list-page-header">
                    <h1 class="px-5 py-3 page-heading">Registration Builder</h1>
                </div>
                <button class="text-button no-bg weight-600" style="margin-right: 10px; background: transparent">
                    <router-link class="text-link margin-0" :to="{ name: 'RegistrationBuilder' }">
                        <img src="@/assets/px/add.png" alt="" style="height: 20px" /> Create new Registration
                    </router-link>
                </button>
            </div>
            <div class="w-100 bg-white" v-if="loading">
                <div class="d-grid justify-content-center align-items-center py-5" style="min-height: 400px">
                    <h4>Fetching information...</h4>
                </div>
            </div>
            <div
                v-else-if="registration_list && registration_list.length > 0"
                class="dashboard-list-table profile-listing-body pb-2"
                style="width: 82vw"
            >
                <b-table
                    id="my-table"
                    :items="registration_list"
                    :fields="fields"
                    :current-page="currentPage"
                    :per-page="perPage"
                    bordered
                    responsive
                >
                    <template #cell(s_no)="row">
                        {{ (currentPage - 1) * perPage + row.index + 1 }}
                    </template>
                    <template #cell(name)="row">
                        <span :id="`regName-${row.index}`" class="column-ellipsis">
                            {{ row.item.name }}
                        </span>
                        <b-tooltip :target="`regName-${row.index}`" triggers="hover" placement="bottom">
                            {{ row.item.name }}
                        </b-tooltip>
                    </template>
                    <template #cell(application)="row">
                        {{ row.item.application }}
                    </template>
                    <template #cell(created_at)="row">
                        {{ formattedDate(row.item.created_at) }}
                    </template>
                    <template #cell(action)="row">
                        <div class="d-flex justify-content-center align-items-center gap-2">
                            <ejs-tooltip
                                class="tooltipcontainer"
                                content="Edit"
                                target="#target"
                                cssClass="customtooltip"
                            >
                                <img
                                    src="@/assets/px/edit-icon.png"
                                    alt=""
                                    style="height: 20px"
                                    id="target"
                                    @click="editRegistration(row.item)"
                                />
                            </ejs-tooltip>
                            <ejs-tooltip
                                class="tooltipcontainer"
                                content="Delete"
                                target="#target"
                                cssClass="customtooltip"
                            >
                                <img
                                    src="@/assets/px/delete-icon.png"
                                    alt=""
                                    style="height: 20px"
                                    id="target"
                                    @click="showDeleteAlertPopUp(row.item.id)"
                                />
                            </ejs-tooltip>
                        </div>
                    </template>
                </b-table>
                <div class="b-table-pagination">
                    <div class="pagination-count-per-page">
                        <div class="pagination-number-per-page">
                            <div>
                                <span>Per page:</span>
                            </div>
                            <div>
                                <select @change="changeCountPerPage" :value="perPage">
                                    <option v-for="item in perPageOptions" :key="item" :value="item">
                                        {{ item }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <b-pagination
                        align="right"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        aria-controls="my-table"
                        first-text="First"
                        prev-text="Prev"
                        next-text="Next"
                        last-text="Last"
                    ></b-pagination>
                </div>
            </div>
            <div class="w-100 bg-white" v-else-if="!registration_list || registration_list.length === 0">
                <div class="d-grid justify-content-center align-items-center py-5" style="min-height: 400px">
                    <h4>No Registration found</h4>
                </div>
            </div>
            <div class="profile-delete-modal">
                <AlertDeleteRegistrationTemplate
                    v-if="showDeleteAlert"
                    :open="showDeleteAlert"
                    @cancel="showDeleteAlert = false"
                    @handleDelete="handleDelete"
                />
            </div>
        </div>
        <Loading v-if="loading || deleting" />
    </div>
</template>

<script>
    import AlertDeleteRegistrationTemplate from './delete_registration_alert.vue';
    import Loading from '@/components/general/loading/loading.vue';
    import dayjs from '@/util/dayjs';
    import { registrationConfig } from '@/util/apiRequests';

    export default {
        name: 'RegistrationBuilderList',
        components: { AlertDeleteRegistrationTemplate, Loading },
        data() {
            return {
                registration_list: null,
                showDeleteAlert: false,
                indexTodelete: null,
                fields: [
                    { key: 's_no', label: 'S NO.', thStyle: { 'padding-left': '0 !important' }, class: 'table-col' },
                    { key: 'name', label: 'Registration Name', class: 'table-col' },
                    { key: 'created_at', label: 'Created Date & Time', class: 'table-col' },
                    { key: 'action', label: 'Action', class: 'action-sticky', stickyColumn: true, isRowHeader: true },
                ],
                totalRows: 0,
                perPageOptions: [5, 10, 20, 50, 100],
                currentPage: 1,
                perPage: 10,
                loading: true,
                deleting: false,
            };
        },
        methods: {
            async getRegistrationList() {
                try {
                    const endpoint = registrationConfig.getAll();
                    const response = await this.$api.get(endpoint);
                    const data = Array.isArray(response.data)
                        ? response.data
                        : response.data.info || response.data.data || [];
                    this.registration_list = Array.isArray(data) ? data : [];
                    this.totalRows = this.registration_list.length;
                } catch (err) {
                    console.error(err);
                    this.$toasted.error('Failed to fetch registrations');
                    this.registration_list = [];
                } finally {
                    this.loading = false;
                }
            },
            formattedDate(date) {
                return date ? dayjs(date).format('MM/DD/YYYY hh:mmA') : '';
            },
            editRegistration(item) {
                this.$router.push({
                    name: 'RegistrationBuilder',
                    query: { id: item.id, application: item.application },
                });
            },
            showDeleteAlertPopUp(id) {
                this.showDeleteAlert = true;
                this.indexTodelete = id;
            },
            async handleDelete() {
                this.showDeleteAlert = false;
                this.deleting = true;
                try {
                    const response = await this.$api.delete(registrationConfig.delete(this.indexTodelete));
                    console.log('[RegBuilder] Delete response:', JSON.stringify(response.data));
                    await this.getRegistrationList();
                    this.$toasted.success('Registration deleted successfully!');
                } catch (err) {
                    console.error('Failed to delete registration', err);
                    this.$toasted.error('Failed to delete registration');
                } finally {
                    this.deleting = false;
                }
            },
            changeCountPerPage({ target: { value } }) {
                this.perPage = Number(value);
            },
        },
        async created() {
            await this.getRegistrationList();
        },
    };
</script>

<style scoped>
    .bg-color {
        background-color: rgba(255, 255, 255, 0.9);
    }

    .text-button a,
    .text-button a:hover,
    .text-button:hover {
        background-color: #5057c3;
        color: white;
        padding: 10px;
        border-radius: 10px;
    }

    .list-page-header {
        display: block;
        margin: 0;
        padding: 0;
        background: none;
    }
</style>

<style>
    .dashboard-list-table table tbody tr > td:first-child {
        padding-left: 2.5em !important;
    }

    .dashboard-list-table .b-table-sticky-column:last-child {
        right: 0;
        min-width: 100px;
    }

    .dashboard-list-table .action-sticky {
        min-width: 150px;
        box-shadow: -10px 0px 10px 1px #aaaaaa;
    }

    .dashboard-list-table .table-col {
        max-width: 150px;
        text-overflow: ellipsis;
        text-wrap: nowrap;
    }
</style>
